/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addClass, hasClass } from '@grapecity/wijmo';
import { DataMap, CellType, CellRangeEventArgs } from '@grapecity/wijmo.grid';
import { Workbook } from '@grapecity/wijmo.xlsx';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Button } from '@mui/material';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { GlobalBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { useMessageBar } from 'hooks/useMessageBar';
import { Code } from 'models/common/CommonCode';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { CommonUtil } from 'utils/CommonUtil';
import { findMdBldoList } from 'apis/md/MdBldo';
import { findMdLineList, saveMdLineList } from 'apis/md/MdLine';
import { MdBldo } from 'models/md/MdBldo';
import { CrudCode } from 'models/common/Edit';
import { MdLine, MdLineCondition } from 'models/md/MdLine';
import { tabs } from 'components/layouts/Tab';
import PropTypes from 'prop-types';
import { IconButton } from 'components/buttons/IconSVG';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import CustomGrid from 'components/grids/CustomGrid';
import { useLoading } from 'components/process/Loading';
import { getExcelFileName } from 'utils/ExcelUtil';
import { hasRole } from 'utils/SessionUtil';

/**
 * @desc 투자비관리 > 기준정보 > 생산라인 등록/조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.12   jshwang12       신규 생성
 */
const MdPrdnLnInqPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openLoading, isLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const initCondition = {
    bildPlntCds: [],
    bldoId: '',
  };
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<MdLineCondition>({ ...initCondition });
  const [rowData, setRowData] = useState<MdLine[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const dragIndex = useRef<any>();

  const tabMenus = useMemo(() => {
    return [
      { id: 'E', name: t('md.label.전극', '전극') },
      { id: 'C', name: t('md.label.셀', '셀') },
      { id: 'M', name: t('md.label.모듈', '모듈') },
    ];
  }, []);

  useEffect(() => {
    getCommonCodes();
    handleSearch();
  }, []);

  useEffect(() => {
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
  }, []);

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * 행추가
   */
  const handleAddRow = () => {
    if (!tabMenus[currentTabIndex]?.id) {
      openMessageBar({
        type: 'error',
        content: t(
          'md.label.생상라인유형코드를 찾을 수 없습니다.',
          '생상라인유형코드를 찾을 수 없습니다.'
        ),
      });
      return;
    }
    const prdnLnTpCd = tabMenus[currentTabIndex].id;
    const newRow = {
      crudKey: CrudCode.CREATE,
      prdnLnTpCd: prdnLnTpCd,
    };
    // 순서 변경된 경우 변경된 순서 유지 후 행추가
    setRowData((prev) => {
      const others = prev.filter((o) => o.prdnLnTpCd !== prdnLnTpCd);
      return [...others, ...[newRow, ...(gridRef.current.collectionView.sourceCollection || [])]];
    });
  };

  /**
   * 행삭제
   */
  const handleDelRow = () => {
    const selectedRows = gridRef.current?.rows?.filter((r) => r.isSelected);

    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }
    const selectedIds = selectedRows.map((row) => parseInt(row.index!));
    const selectedObjs = selectedIds.map((index) => filterRowData[index]);

    setRowData((prev) => {
      return prev.reduce((acc: MdLine[], cur) => {
        const isSelected = selectedObjs.some((selected) => selected === cur);
        if (isSelected) {
          if (cur.crudKey !== CrudCode.CREATE) {
            acc.push({ ...cur, crudKey: CrudCode.DELETE });
          }
        } else {
          acc.push(cur);
        }
        return acc;
      }, []);
    });
  };

  /**
   * 저장
   */
  const handleSave = () => {
    openLoading(true);
    const prdnLnTpCd = tabMenus?.[currentTabIndex]?.id;
    let rows = rowData;
    if (prdnLnTpCd) {
      // 현재 변경된 순서내용 저장
      const others = (rows || []).filter((o) => o.prdnLnTpCd !== prdnLnTpCd);
      rows = [...others, ...(gridRef.current.collectionView.sourceCollection || [])];
    }
    saveMdLineList(rows)
      .then((res) => {
        openMessageBar({
          type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
          content:
            res?.successOrNot === 'Y'
              ? t('com.label.저장되었습니다.', '저장되었습니다.')
              : res.data && typeof res.data === 'string'
              ? res.data
              : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
        });
        if (res?.successOrNot === 'Y') {
          handleSearch();
        }
      })
      .finally(() => openLoading(false));
  };

  const handleDownloadExcel = () => {
    findMdLineList(condition).then((result) => {
      const workbook = new Workbook();
      for (const tab of tabMenus || []) {
        const ws = CommonUtil.createWijmoSheet(
          tab.name,
          mapLayoutDefinition[tab.id],
          (result || []).filter((o) => o.prdnLnTpCd === tab.id)
        );
        workbook.sheets.push(ws);
      }
      workbook.saveAsync(getExcelFileName(t('md.label.생산라인 목록', '생산라인 목록')));
    });
  };

  const handleSearch = () => {
    findMdLineList(condition).then((result) => setRowData(result));
  };

  const getCommonCodes = async () => {
    const bildPlntCd: Code[] = await getCommonCodeNames('BILD_PLNT_CD'); // 건설플랜트코드
    const bldoList: MdBldo[] = await findMdBldoList({});

    setCode({
      bildPlntCd: bildPlntCd,
      bldoCd: (bldoList || []).map((o) => ({
        cmnCd: o.bldoId,
        cmnCdNm: o.bldoNm,
      })),
    });
  };

  const cellLayoutDefinition = [
    {
      header: String(t('md.label.TYPE', 'TYPE')),
      columns: [
        {
          binding: 'fertLenTpNm',
          header: String(t('md.label.전장', '전장')),
          width: '*',
          minWidth: 80,
          align: 'left',
        },
        {
          binding: 'tabDirCtn',
          header: String(t('md.label.TAB 방향', 'TAB 방향')),
          width: '*',
          minWidth: 80,
          align: 'left',
        },
        {
          binding: 'stakFoldEqpNm',
          header: String(t('md.label.Folding/Stack', 'Folding/Stack')),
          width: '*',
          minWidth: 80,
          align: 'left',
        },
      ],
    },
    {
      binding: 'eqpSpedCtn',
      header: String(t('md.label.생산속도(PPM)', '생산속도(PPM)')),
      width: 120,
      minWidth: 80,
      align: 'left',
    },
    {
      binding: 'fertGenDivsNm',
      header: String(t('md.label.설비세대', '설비세대')),
      width: 120,
      minWidth: 80,
      align: 'left',
    },
    {
      binding: 'prdnMdlNm',
      header: String(t('md.label.생산모델', '생산모델')),
      width: 120,
      minWidth: 80,
      align: 'left',
    },
  ];

  const moduleLayoutDefinition = [
    {
      binding: 'mduStrcTpNm',
      header: String(t('md.label.TYPE', 'TYPE')),
      width: 120,
      minWidth: 80,
      align: 'left',
    },
    {
      binding: 'prdnMdlNm',
      header: String(t('md.label.생산모델', '생산모델')),
      width: 120,
      minWidth: 80,
      align: 'left',
    },
    {
      binding: 'tatmCtn',
      header: String(t('md.label.Tact Time', 'Tact Time')),
      width: 120,
      minWidth: 80,
      align: 'left',
    },
    {
      header: String(t('md.label.Size', 'Size')),
      columns: [
        {
          binding: 'cellSizeCtn',
          header: String(t('md.label.셀', '셀')),
          width: '*',
          minWidth: 80,
          align: 'left',
        },
        {
          binding: 'mduSizeCtn',
          header: String(t('md.label.모듈', '모듈')),
          width: '*',
          minWidth: 80,
          align: 'left',
        },
        {
          binding: 'pakSizeCtn',
          header: String(t('md.label.팩', '팩')),
          width: '*',
          minWidth: 80,
          align: 'left',
        },
      ],
    },
    {
      header: String(t('md.label.수량', '수량')),
      columns: [
        {
          binding: 'cellQtyCtn',
          header: String(t('md.label.셀/모듈', '셀/모듈')),
          width: '*',
          minWidth: 80,
          align: 'left',
        },
        {
          binding: 'pakQtyCtn',
          header: String(t('md.label.모듈/팩', '모듈/팩')),
          width: '*',
          minWidth: 80,
          align: 'left',
        },
      ],
    },
  ];

  const mapLayoutDefinition = useMemo(() => {
    return (tabMenus || []).reduce((acc, cur) => {
      return {
        ...acc,
        [cur.id]: [
          {
            binding: 'crudKey',
            header: String(t('com.label.상태', '상태')),
            width: 43,
            minWidth: 45,
            align: 'center',
            isReadOnly: true,
            cellTemplate: GridStatusCellTemplate,
          },
          {
            binding: 'sortable',
            header: String(t('com.label.순서', '순서')),
            minWidth: 43,
            width: 43,
            visible: hasAuth,
            align: 'center',
            isReadOnly: true,
            cssClass: 'WijmoCellMove',
            cellTemplate: '<Button />',
          },
          {
            binding: 'bildPlntCd',
            header: String(t('com.label.플랜트', '플랜트')),
            width: 150,
            minWidth: 80,
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.bildPlntCd || [], 'cmnCd', 'cmnCdNm'),
          },
          {
            binding: 'bldoId',
            header: String(t('com.label.동', '동')),
            width: 150,
            minWidth: 80,
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.bldoCd || [], 'cmnCd', 'cmnCdNm'),
          },
          {
            binding: 'prdnLnNm',
            header: String(t('com.label.생산라인', '생산라인')),
            width: cur.id === 'E' ? '*' : 150,
            minWidth: 80,
            align: 'left',
          },
          ...(cur.id === 'C' ? cellLayoutDefinition : cur.id === 'M' ? moduleLayoutDefinition : []),
          {
            binding: 'dataUpdUserNm',
            header: String(t('com.label.수정자', '수정자')),
            width: 150,
            minWidth: 80,
            align: 'center',
            isReadOnly: true,
          },
          {
            binding: 'dataUpdDtm',
            header: String(t('com.label.수정일자', '수정일자')),
            width: 150,
            minWidth: 80,
            align: 'center',
            isReadOnly: true,
          },
        ],
      };
    }, {});
  }, [tabMenus, code]);

  const filterRowData = useMemo(() => {
    const prdnLnTpCd = tabMenus?.[currentTabIndex]?.id;
    if (prdnLnTpCd) {
      return (rowData || []).filter((o) => o.prdnLnTpCd === prdnLnTpCd);
    }
    return [];
  }, [rowData, currentTabIndex]);

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      const item = e.panel.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          // 이미 사용중인 항목인 경우 선택불가/readonly
          if (item?.usedLnYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
      const binding = s.columns[e.col].binding;
      if (s.cells === e.panel) {
        if ('sortable' === binding) {
          e.cell.draggable = true;
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.usedLnYn === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });

    grid.hostElement.addEventListener('dragstart', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggingRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
    grid.hostElement.addEventListener('dragover', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        e.preventDefault();
      }
    });
    grid.hostElement.addEventListener('drop', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggedRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('md.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={code?.bildPlntCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCds || []}
                  onChange={(value) => handleOnChange('bildPlntCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.동', '동'))}</label>
                <ComboBox
                  options={code?.bldoCd}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition.bldoId}
                  msgError={String(t('com.msg.동을 선택해 주세요.', '동을 선택해 주세요.'))}
                  onChange={(value) => handleOnChange('bldoId', value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={handleSearch}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>

      <div css={tabs.wrap} style={{ marginTop: '24px' }}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => {
            // 탭 변경 시 순서 변경내용 rowData에 반영
            const prdnLnTpCd = tabMenus?.[currentTabIndex]?.id;
            if (prdnLnTpCd) {
              setRowData((prev) => {
                const others = prev.filter((o) => o.prdnLnTpCd !== prdnLnTpCd);
                return [...others, ...(gridRef.current.collectionView.sourceCollection || [])];
              });
            }
            setCurrentTabIndex(newValue);
          }}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`md-prdnLnInq-tab-${idx}`}
              aria-controls={`md-prdnLnInq-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
        <div css={tabs.btn}>
          {hasAuth && (
            <>
              <Button css={IconButton.button} className="plus" onClick={handleAddRow} disableRipple>
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="minus"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={handleSearch}>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </div>
        <TabPanel value={currentTabIndex} index={currentTabIndex}>
          <>
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('md.label.생산라인 목록', '생산라인 목록')}</h3>
                <span className="total">
                  {t('com.label.총', '총')}
                  <span>{(filterRowData || []).length.toLocaleString()}</span>
                  {t('com.label.건', '건')}
                </span>
              </SubTitleGroup>
            </SubTitleLayout>
            <CustomGrid
              layoutDefinition={mapLayoutDefinition[tabMenus[currentTabIndex].id]}
              rowData={filterRowData}
              initialized={onInitialized}
              height={450 + (currentTabIndex === 0 ? 36 : 0)}
              allowPinning={false}
              allowSorting={false}
              isReadOnly={!hasAuth}
              excludeFilter={['sortable']}
              draggingRow={(grid, e) => {
                dragIndex.current = e.row;
                grid.collectionView.moveCurrentToPosition(e.row);
              }}
              draggedRow={(grid, e) => {
                const dropIndex = e.row;
                const arr = grid.collectionView.sourceCollection;
                grid.collectionView.deferUpdate(() => {
                  const item = arr[dragIndex.current];
                  arr.splice(dragIndex.current, 1);
                  arr.splice(dropIndex, 0, item);
                  grid.collectionView.moveCurrentToPosition(dropIndex);
                  if (item.crudKey !== CrudCode.CREATE) {
                    item.crudKey = CrudCode.UPDATE;
                  }
                });
              }}
            />
          </>
        </TabPanel>
        {hasAuth && (
          <GlobalBtnGroup>
            <Button
              css={IconButton.button}
              className="save"
              onClick={handleSave}
              disabled={isLoading}
              disableRipple
            >
              {t('com.button.저장', '저장')}
            </Button>
          </GlobalBtnGroup>
        )}
      </div>
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`md-prdnLnInq-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default MdPrdnLnInqPage;
